import { StyledTab, StyledTabs } from "@/components/ui/mui/StyledTabs";
import {
  AlertType,
  Client,
  DocumentRead,
  DocumentRecordType,
  ExtractedRecord,
  ResourceName,
  TargetSchema,
} from "@/types";
import { ENTITY_GROUPINGS, reorderSchemasForGrouping, TOP_ROW_PRIORITY_COLUMNS } from "../../utils/documentGrouping";
import { buildPdfHighlightsForRecord } from "../../utils/pdfHighlights";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Switch,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Warning as WarningIcon, Error as ErrorIcon, Close as CloseIcon } from "@mui/icons-material";
import { BORDER_RADIUS } from "@/constants/layout";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRecordContext, useGetOne, useRefresh, useDataProvider, useNotify, useRedirect } from "react-admin";
import { FieldMappingDialog } from "@/resources/documents/components/field-mapping/FieldMappingDialog";
import { ClientGate } from "./ClientGate";
import { StickyApproveBar } from "./StickyApproveBar";
import { BeforeYouApproveRail } from "./BeforeYouApproveRail";
import { ApproveConfirmDialog } from "./ApproveConfirmDialog";
import { reprocessConfirmCopy } from "./ReviewActions";
import { deriveApproveStatus } from "../../utils/approveStatus";
import {
  buildReviewChecks,
  CHECK_PAID_THROUGH,
  type ErroredReasonGroupInput,
  type PlacedAcknowledgement,
  advisoryCheckKey,
} from "../../utils/reviewChecks";
import { reconcileReportedYtd, type YtdMismatch } from "../../utils/ytdReconciliation";
import { buildApproveSummary, buildMappingRecap } from "../../utils/approveSummary";
import { useReviewAcknowledgements } from "../../hooks/useReviewAcknowledgements";
import { useReviewAsOfState } from "../../hooks/useReviewAsOfState";
import { parseMoneyCell } from "../../utils/amounts";
import type { IncomingClaimLine } from "@/resources/stoploss-claims/components/StoplossClaimPreviewDialog";
import { ConfirmationDialog } from "@/components/ui/ConfirmationDialog";
import { CancelLabelType, ColorType, ConfirmLabelType } from "@/types/shared/uiComponents";
import { RejectDocumentDialog } from "./RejectDocumentDialog";
import { DeleteImpactDialog } from "../DeleteImpactDialog";
import { useCascadeDeletePermission } from "../../hooks/useCascadeDeletePermission";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { usePartner } from "@/providers/partner/PartnerProvider";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { DocumentReviewHeader } from "./DocumentReviewHeader";
import { ReviewContextBar, resolvedPolicyLabel, useFallbackPolicyLabel } from "./ReviewContextBar";
import { featureFlags } from "@/utils/featureFlags";
import { SourcePreviewDrawer } from "./SourcePreviewDrawer";
import { PdfSourcePreviewDrawer } from "./PdfSourcePreviewDrawer";
import { SCHEMA_LABELS } from "../../utils/reviewUtils";
import { useFieldMappingReviewValidation } from "../../hooks/useFieldMappingState";
import { useCriticalFieldAdvisory } from "../../hooks/useCriticalFieldAdvisory";
import { useSourcePreview } from "../../hooks/useSourcePreview";
import { unresolvedErroredForGate, unresolvedErroredFromServer } from "../../utils/erroredGating";
import { absoluteIndexOf, expandableRowKey } from "../../utils/recordIndexing";
import { getLinkedRecords as resolveLinkedRecords } from "../../utils/recordLinking";
import { useRecordExclusions } from "../../hooks/useRecordExclusions";
import { usePdfSourcePreview } from "../../hooks/usePdfSourcePreview";
import { useErroredRecords } from "../../hooks/useErroredRecords";
import { OUTSIDE_POLICY_KIND, buildOutsidePolicyGroups, policyWindowsLabel } from "../../utils/outsidePolicy";
import { assessPaidDateCoverage } from "../../utils/paidDateCoverage";
import { claimItemsByReasonPath } from "@/resources/stoploss-claims/claimItems/ineligibleReasons";
import { LinkedRecordsDisclosure } from "./LinkedRecordsDisclosure";
import { selectRowDecisions } from "./RowDecisionsNotice";
import { RecordReviewGrid } from "./RecordReviewGrid";
import { isSpreadsheetDocument, isPdfDocument } from "../../utils/documentFormat";
import { ExpandedRecordDetails, hasExpandableContent } from "./ExpandedRecordDetails";
import { buildDynamicColumns, selectClaimantNameColumn } from "./reviewGridColumns";
import { useDocumentTransformationData } from "../../hooks/useDocumentTransformationData";
import { useReviewApproval } from "../../hooks/useReviewApproval";
import { useReviewRecords } from "../../hooks/useReviewRecords";
import { useReviewProjection } from "../../hooks/useReviewProjection";
import {
  REVIEW_AUTOLOAD_MAX_RECORDS,
  REVIEW_PROJECTION_WINDOW_MAX_RECORDS,
  useTransformationRecords,
} from "../../hooks/useTransformationRecords";
import { derivedColumnsAvailable, deriveProjectionStrategy } from "../../utils/projectionGate";
import { useReviewProjectionLookups } from "../../hooks/useReviewProjectionLookups";
import { useReimbursementSchemaRendering } from "../../hooks/useReimbursementSchemaRendering";
import { useDocumentClassification } from "../../hooks/useDocumentClassification";
import {
  getRecordTransformationErrors,
  getRecordValidationErrors,
  getRecordSchemas,
  getRecordData,
} from "../../utils/recordUtils";
import { DATE_FORMATS, formatDate } from "@/utils/formatters/dateFormatter";
import { summarizeRecords } from "../../utils/recordSummary";
import { CompactTableColumn } from "@/components/common/CompactTable";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Severity tag used for approval gating + per-tab badge icons. Record-level
 * errors are surfaced via tab badges and the approve-button gating only;
 * mapping-grade signals (blocking validation, required-tier critical-field
 * gaps) live in the sidebar's Mapping Review card.
 */
type RecordIssueSeverity = "error" | "warning";

const PDF_MIME_TYPES = new Set(["application/pdf"]);
const PDF_EXTENSIONS = /\.pdf$/i;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** Width of the source-preview side drawer (PDF or spreadsheet). */
export const SOURCE_PREVIEW_WIDTH = 650;

interface DocumentReviewTabProps {
  /** Whether the source preview drawer is currently open — owned by the parent so it can constrain sibling widths. */
  sourcePreviewOpen: boolean;
  onSourcePreviewChange: (open: boolean) => void;
}

/** A group awaiting the exclude confirm: errored rows (server read) or outside-policy claim lines (loaded). */
interface PendingExclude {
  label: string;
  recordIndices: number[];
  source: "errored" | "loaded";
  /** Why excluding might be the wrong move, named in the confirm. */
  why: string;
  /**
   * Walk linked records (claimant → its lines). False for claim-line groups:
   * the linkage is symmetric on the claimant key, so cascading from a line
   * would take the claimant and every sibling line (#7436 walkthrough).
   */
  cascade: boolean;
}

export const DocumentReviewTab = ({ sourcePreviewOpen, onSourcePreviewChange }: DocumentReviewTabProps) => {
  const documentRecord = useRecordContext<DocumentRead>();

  const [selectedTab, setSelectedTab] = useState(0);
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  // Reject requires a reason (#7441): the dialog owns the reason/note and
  // hands them to handleReject; More → "Reject file…" and ClientGate's
  // "Wrong file? Reject it…" both open it.
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  // Approve confirm, ALWAYS shown (#7440): the rail's Approve and the sticky
  // bar's Approve both open it; nothing approves directly.
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  // Reprocess from the rail's "Fix the extraction" (#7432) — same confirm copy
  // as the More menu's item (reprocessConfirmCopy).
  const [railReprocessConfirmOpen, setRailReprocessConfirmOpen] = useState(false);
  // More → "Delete document…" (#7441 AC1): same DeleteImpactDialog as the show
  // page header, offered only with documents:cascade-delete.
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { hasCapability: canCascadeDelete } = useCascadeDeletePermission();
  const redirect = useRedirect();
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  // Default view is critical-only columns. Reviewer can toggle to see populated
  // non-critical columns when context is needed (provider name, service dates,
  // etc.). State is per-document — resets when navigating to a different doc
  // since the component remounts.
  const [showAllColumns, setShowAllColumns] = useState(false);

  // Auto-populate selectedClient when document already has a client_id_ref (e.g. uploaded from client screen)
  const { data: existingClient } = useGetOne<Client>(
    "clients",
    { id: documentRecord?.client_id_ref ?? "" },
    { enabled: !!documentRecord?.client_id_ref && !selectedClient }
  );
  useEffect(() => {
    if (existingClient && !selectedClient) {
      setSelectedClient(existingClient);
    }
  }, [existingClient, selectedClient]);

  // Source preview record state (which record is focused in the drawer)
  const [sourcePreviewRecord, setSourcePreviewRecord] = useState<ExtractedRecord | null>(null);

  const clientId = documentRecord?.client_id_ref ?? selectedClient?.id;

  const { latestTransformation, template, isLoading, hasTransformation } = useDocumentTransformationData({
    documentId: documentRecord?.id,
  });

  // Records arrive from the windowed endpoint, not on the transformation row
  // (#6755). Below REVIEW_AUTOLOAD_MAX_RECORDS every page loads before the
  // surface reports ready, so small documents behave exactly as before; above
  // it, the first page renders and the rest load on demand.
  const {
    records: loadedRecords,
    total: totalStoredRecords,
    totalKnown: storedRecordCountKnown,
    hasMore: hasMoreRecords,
    loadMore: loadMoreRecords,
    isReady: recordsReady,
    isLoading: isLoadingRecords,
    error: recordsError,
  } = useTransformationRecords({
    documentId: documentRecord?.id,
    transformationId: latestTransformation?.id,
    enabled: !!latestTransformation?.id,
  });

  // Linked-record predicate — the pure logic lives in recordLinking.ts, where
  // tests pin it (#6830); this binds it to the loaded records.
  const getLinkedRecords = useCallback(
    (record: ExtractedRecord): ExtractedRecord[] => resolveLinkedRecords(record, loadedRecords),
    [loadedRecords]
  );

  // Exclusion state — keyed on absolute record_index, reset when the
  // transformation changes (reprocess-in-place), cascading through linked
  // records. See useRecordExclusions for the full reasoning.
  const {
    excludedIndices,
    excludedRecordIndices,
    reviewerDivergenceCount,
    isRecordExcluded,
    bulkExclude: handleBulkExclude,
    bulkRestore: handleBulkRestore,
  } = useRecordExclusions({
    transformationId: latestTransformation?.id,
    records: loadedRecords,
    getLinked: getLinkedRecords,
  });

  const {
    recordsBySchema,
    availableSchemas: rawAvailableSchemas,
    recordCounts,
    totalRecords,
    requiresClient,
    hasRecords,
  } = useReviewRecords({ transformation: latestTransformation, records: loadedRecords });

  // Reorder schemas so the document-type's top-row schema (e.g. CLAIMANT for stoploss claims) comes first.
  const grouping = documentRecord?.record_type ? ENTITY_GROUPINGS[documentRecord.record_type] : undefined;
  const availableSchemas = useMemo(
    () => reorderSchemasForGrouping(rawAvailableSchemas, grouping),
    [rawAvailableSchemas, grouping]
  );

  // Validation runs over the LOADED records, not transformation.extracted_records
  // — that field is permanently undefined now that the SPA reads the windowed
  // endpoint, and a validator pointed at it returns "no issues" for every
  // schema, silently disarming the approval gate (#6755 review). One loaded
  // record per schema is enough; a schema with no record in the loaded pages
  // reports "not validated" rather than passing.
  const { validationIssues: mappingValidationIssues } = useFieldMappingReviewValidation(
    latestTransformation,
    template,
    availableSchemas,
    loadedRecords,
    !hasMoreRecords
  );

  // Server-computed summary off the transformation row (#6755) — feeds the
  // header total below and the projection gate further down.
  const recordSummary = latestTransformation?.processing_metadata?.record_summary;

  // Header total. useReviewRecords falls back to counting the LOADED records
  // when the transformation has no server summary (pre-#6755, no backfill
  // migration exists) — fine when everything is loaded, but on a paged
  // summary-less document it reads "1,000 records extracted" directly above a
  // footer correctly saying "Showing 1,000 of 497,711" (#6755 review). Use the
  // endpoint's whole-set count instead there. It is a true record count rather
  // than the summary's per-schema bucket sum, so it can differ from a summary
  // total on multi-schema documents — a known-honest number beating a wrong
  // one is the point.
  const displayTotalRecords = !recordSummary && hasMoreRecords ? totalStoredRecords : totalRecords;

  const isClientMissing = requiresClient && !selectedClient;
  const isReadyForApproval = !isClientMissing && mappingValidationIssues.length === 0;

  // Review projection — per-claimant forecast of what approval will do. Fires
  // once client is resolved; re-fetches when client changes.
  const resolvedClientId = documentRecord?.client_id_ref ?? selectedClient?.id;

  // Projection strategy for this document — whole / windowed / skipped. The
  // derivation (and the reasoning: fail closed on unknown size, prefer the
  // summary on the transformation row, fall back to the endpoint's count once
  // known) lives in projectionGate.ts where it is unit-tested. The load-bearing
  // property is that an UNKNOWN size skips: `total` initialises to 0, and
  // 0 > threshold reading as "small" on the render where the transformation
  // lands is precisely how the unwindowed 2.34 GiB projection (#6826) would
  // fire on the document the gate exists for (#6755 review).
  const projectionStrategy = deriveProjectionStrategy({
    summary: recordSummary,
    endpointTotal: storedRecordCountKnown ? totalStoredRecords : null,
  });

  const isProjectionWindowed = projectionStrategy === "windowed";
  const isProjectionSkipped = projectionStrategy === "skipped";

  // Covers the records actually loaded, capped at the server's own window
  // clamp so the two agree by construction rather than by assumption. Past the
  // cap (~10 "Load more" clicks) newly loaded rows have no forecast — degraded
  // to ABSENCE, not misassignment, because forecasts join on the absolute
  // record_index — and forecastCoveredRecords below is what tells the reviewer.
  const projectionWindow = useMemo(() => {
    if (!isProjectionWindowed || loadedRecords.length === 0) return undefined;
    const first = absoluteIndexOf(loadedRecords[0]);
    if (first === undefined) return undefined;
    return {
      fromRecordIndex: first,
      limit: Math.min(loadedRecords.length, REVIEW_PROJECTION_WINDOW_MAX_RECORDS),
    };
  }, [isProjectionWindowed, loadedRecords]);

  // Non-null when the windowed forecast layer covers only a prefix of what is
  // loaded — the count to disclose next to "Load more".
  const forecastCoveredRecords =
    isProjectionWindowed && loadedRecords.length > REVIEW_PROJECTION_WINDOW_MAX_RECORDS
      ? REVIEW_PROJECTION_WINDOW_MAX_RECORDS
      : null;
  const {
    projection: reviewProjection,
    isLoading: isProjectionLoading,
    error: projectionError,
  } = useReviewProjection({
    documentId: documentRecord?.id,
    clientId: resolvedClientId,
    // Skipped above REVIEW_PROJECTION_MAX_RECORDS (#6755).
    //
    // POST /documents/{id}/review-projection materialises the transformation's
    // ENTIRE record set inside mgmt-api — measured 2.34 GiB peak on a
    // 497,711-record document, against a container declared 1Gi, and that run
    // took the cheaper of the two branches (#6826). Requesting it here is how
    // the review tab takes the API down for everyone.
    //
    // Consequence, stated plainly because it is a real loss: on a document over
    // the threshold with claimants that have MORE THAN ONE ROW, the reviewer
    // gets the grid and the source preview but NO forecast layer — no YTD
    // before/after, no will-create/on-file, no deductible context. Being able
    // to inspect the records at all is the thing that was blocking review
    // entirely.
    //
    // THIS COMMENT USED TO SAY "those return when #6826 lands". They do not.
    // #6826 bounded the read server-side (409 DOC-000002) so a caller cannot
    // OOM mgmt-api by asking — it did not make the whole-document forecast
    // affordable, and nothing about a bound could. Corrected here rather than
    // deleted, because a promise in a comment is why nobody went looking.
    //
    // What is actually still missing is narrower than "large documents", and
    // worse than it sounds. Windowed forecasts cover the > 25,000 case, but
    // only when max_rows_per_claimant == 1: ytd_delta sums paid_amount across
    // ALL of a claimant's rows, so a window edge that splits a claimant
    // understates the figure with no signal, and the guard fails closed. Most
    // real documents are multi-row (locally 16, 10, 7 and 2 rows per
    // claimant), so the common shape gets nothing once it crosses the
    // threshold. Restoring it needs claimant-boundary windowing, or a
    // ytd_delta computed server-side over the claimant's full row set — a
    // design change, tracked separately.
    enabled: hasRecords && !isClientMissing && !isProjectionSkipped,
    window: projectionWindow,
  });

  // Reviewer-confirmable paid-through (as-of) date for carrier YTD / aggregate
  // documents (ADR-051). The value lives here because it feeds the accept call;
  // the gate / stale / disabled derivations live in DocumentReviewHeader. Pre-
  // filled from the header-parsed default when present, but always editable —
  // manual entry works even when extraction found none.
  const [reportedAsOf, setReportedAsOf] = useState("");
  const [asOfTouched, setAsOfTouched] = useState(false);
  useEffect(() => {
    if (!asOfTouched && !reportedAsOf && reviewProjection?.default_reported_as_of) {
      setReportedAsOf(reviewProjection.default_reported_as_of);
    }
  }, [reviewProjection?.default_reported_as_of, asOfTouched, reportedAsOf]);
  const handleAsOfChange = useCallback((v: string) => {
    setAsOfTouched(true);
    setReportedAsOf(v);
  }, []);

  // Clear an AUTO-FILLED as-of when the transformation changes — but never a
  // reviewer-typed one (!4711 review, [AEM-asof-not-discarded]).
  //
  // The effect above only fills when `!reportedAsOf`, so a value auto-filled
  // from run N blocks run N+1's `default_reported_as_of` forever: the reviewer
  // ends up posting a date derived from a superseded extraction (ADR-051 claims
  // period). Clearing the untouched case lets the new run's default land.
  //
  // A date the reviewer TYPED is deliberately kept. It is their assertion about
  // the carrier's reporting period, which is a property of the file, not of one
  // extraction run — a reprocess re-reads the same file. This is also why the
  // reprocess confirm no longer claims the date is discarded: it isn't.
  //
  // Only when the transformation actually CHANGES. On mount the state is
  // already "", and an unconditional clear here raced the fill effect above
  // whenever the projection was already in hand on first render (the default
  // landed and was wiped in the same commit) — #7439 made that visible once
  // the defaulted date had a card to show.
  const seenTransformationId = useRef(latestTransformation?.id);
  useEffect(() => {
    if (seenTransformationId.current === latestTransformation?.id) return;
    seenTransformationId.current = latestTransformation?.id;
    if (!asOfTouched) setReportedAsOf("");
    // Keyed on the transformation only; including asOfTouched would re-clear
    // on every toggle.
  }, [latestTransformation?.id]);

  // Document-level context for the field-mapping dialog's per-tab advisory
  // list (critical-fields contract). Drives conditional filtering — e.g.
  // denial_reason fires only when records have a denial_amount; family-unit
  // subscriber id fires only on family-deductible policies. Memoized so the
  // dialog's `useMemo` deps don't churn each render.
  const criticalFieldAdvisoryContext = useMemo(
    () => ({
      extractedRecords: loadedRecords,
      hasFamilyDeductiblePolicy:
        typeof reviewProjection?.family_deductible === "number" && reviewProjection.family_deductible > 0,
    }),
    [loadedRecords, reviewProjection?.family_deductible]
  );

  // Sidebar advisory channel — REQUIRED-tier critical-field gaps only. The
  // dialog still gets the full list (required + recommended) via its own
  // computation; the sidebar narrows to required because that's the tier
  // that produces record-level errors on approve. Recommended-tier gaps
  // belong in the dialog where the reviewer is actively mapping.
  const fullAdvisory = useCriticalFieldAdvisory(template, availableSchemas, criticalFieldAdvisoryContext);
  const requiredAdvisory = useMemo(() => fullAdvisory.filter((a) => a.entry.severity === "required"), [fullAdvisory]);

  // react-admin refresh trigger — used to refetch the document and its
  // dependent resources after a classification-override reprocess settles
  // (AB#4363 Path A). Without this, the reviewer would have to manually
  // refresh to see the new template's mappings + extracted records.
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { canEdit, canCreate, isLoading: permissionsLoading } = useResourcePermissions(ResourceName.DOCUMENTS);
  const { canList: canListClaimLines } = useResourcePermissions(ResourceName.CLAIM_ITEMS);
  const { partner } = usePartner();
  // Approve/Reject POST on documents:create, and the read-only partner guard
  // (ADR-035) rejects the write regardless of role. Either way the header
  // locks and Approve says "View only. A reviewer approves this file."
  // (#7431 AC2/AC4). Not locked while permissions are still resolving, so a
  // reviewer never sees the header flash to plain text on load.
  const isViewOnly = (!permissionsLoading && !canCreate) || !!partner?.readOnly;
  const notifyApiError = useRichApiErrorNotify();

  // Classification banner state (AB#4363). Only fetch once a transformation
  // exists — the override has nowhere to land before that, and the chip
  // would just render an empty state. Reviewer overrides trigger a server-
  // side reprocess; isReprocessing is true while the hook polls for the
  // doc to settle back to review_required, and onReprocessComplete fires
  // a refresh once it does so all dependent UI (records grid, projection,
  // field mappings) auto-update without the reviewer needing to refresh.
  const {
    classification: documentClassification,
    isLoading: isClassificationLoading,
    isSaving: isClassificationSaving,
    isReprocessing: isClassificationReprocessing,
    trackReprocess,
    error: classificationError,
    setOverride: handleClassificationOverride,
  } = useDocumentClassification({
    documentId: documentRecord?.id,
    enabled: hasTransformation,
    onReprocessComplete: refresh,
  });

  // #6920 — a plain reprocess from the review screen.
  //
  // A reprocess was already reachable here, but only by opening Field Mapping
  // and saving (useFieldMappingSave posts to /reprocess). A reviewer acting on
  // DOC-000001 ("the record index is stale — reprocess") does not want to touch
  // mappings, and our own error copy gave them no control with that word on it.
  //
  // Progress is handed to trackReprocess rather than re-implemented, so this
  // settles and refreshes exactly like a classification override: same poll,
  // same spinner, same records/projection/mapping refresh on completion.
  const handleReprocess = useCallback(async () => {
    const docId = documentRecord?.id;
    if (!docId) return;
    // In-flight guard (!4711 review, [AEM-no-inflight-guard]). Two POSTs enqueue
    // two worker runs for one document — ReprocessDocument has no status
    // precondition server-side — and the grid settles on whichever finishes
    // last. trackReprocess cancels the prior POLL; it does nothing about the
    // prior POST. The button is also disabled on this flag; this is the second
    // line of defence for a keyboard or double-fire path.
    if (isClassificationReprocessing) return;
    try {
      await dataProvider.create(`documents/${docId}/reprocess`, {
        data: {
          // RESOLVED id, not the raw ref (!4711 review, [AEM-client-id-unresolved]).
          // resolveClientIDForDocument 400s on an empty ref for a non-multi-client
          // document, so the raw ref meant a reviewer who resolved the client in
          // the ClientGate — the exact path the gate exists for — clicked
          // Reprocess and got "Client ID is required".
          client_id_ref: resolvedClientId,
          reason: "Reviewer reprocessed from the review screen",
          force_reprocess: true,
          // Sending no mappings. A partial map would be worse than useless:
          // UpdateFieldMappings is a wholesale REPLACE, so it would DELETE every
          // mapping not sent.
          //
          // Correction to an earlier version of this comment (!4711 review,
          // [AEM-empty-mappings-pins-template]): "empty, not omitted" is not a
          // distinction the handler makes. Its only branch is
          // `len(FieldMappings) > 0`, so `{}`, null, an omitted key and an empty
          // body all take the same path. `{}` buys nothing over omitting it; it
          // is kept only because it is what the queue path already sends.
          //
          // AND THE else BRANCH IS NOT A NEUTRAL RE-RUN. It calls
          // ConfirmFieldMappings, which sets IsConfirmed = true on EVERY mapping
          // and persists it — after which the worker reuses the prior template
          // because a mapping is confirmed. So a reviewer who reprocesses
          // BECAUSE the extraction looks wrong launders the AI-inferred mappings
          // into "a human confirmed these" and blocks re-inference on every
          // later reprocess. Pre-existing on the `failed` path; #6920 makes it
          // the recommended action from review_required, which is what makes it
          // worth naming. The confirm copy now says the mappings are kept.
          // Forcing re-inference is a product call, filed separately.
          field_mappings: {},
        },
      });
      notify("Document reprocessing started", { type: AlertType.SUCCESS });
      trackReprocess();
    } catch (error) {
      // STD-008 §4 (!4711 review, [AEM-error-handling-std008]). A raw
      // error.message flattens PARTNER_READ_ONLY — read-only partners reach this
      // route, since PartnerWriteGuardMiddleware is mounted on the whole
      // authenticated group and reprocess is in no exemption matcher — and it
      // flattens the #6893 409s (DOC-000001 / DOC-000003) that this PR exists to
      // make actionable. Approve/Reject on this same screen already use this.
      notifyApiError(error);
    }
  }, [
    dataProvider,
    documentRecord?.id,
    resolvedClientId,
    isClassificationReprocessing,
    notify,
    notifyApiError,
    trackReprocess,
  ]);

  // Resolve extracted records to their projection-side data — claimant
  // forecasts, reimbursement summaries (with claimant-identity fallback for
  // deduped grid rows), per-row forecasts, and the survivor set + filter
  // closure that gate the reimbursement views. See the hook for the lookup
  // model and why two summary paths exist (AB#4455 / AB#4495).
  const {
    getProjectionForRecord,
    getSummaryForRecord,
    getReimbursementRowForRecord,
    getClaimItemRowForRecord,
    filterReimbursementSurvivors,
  } = useReviewProjectionLookups({
    projection: reviewProjection,
  });

  // True when this transformation has projection data and we should apply the
  // survivor filter to reimbursement-schema records. Without projection data
  // (loading, error, or non-reimbursement doc) we show the raw record list so
  // the review surface still works.
  const shouldApplyReimbursementSurvivorFilter =
    documentRecord?.record_type === DocumentRecordType.REIMBURSEMENTS &&
    !!reviewProjection?.reimbursement_summaries &&
    !isProjectionLoading;

  // Browser-held acknowledgements of advisory checks (#7432 AC4). Reset when
  // the transformation changes. What the approval SENDS is the subset the
  // rail honours this render (see buildReviewChecks), held here because the
  // approval hook is wired before the rail's inputs are derived.
  const acknowledgements = useReviewAcknowledgements(latestTransformation?.id);
  const [placedAcknowledgements, setPlacedAcknowledgements] = useState<PlacedAcknowledgement[]>([]);

  const { handleApprove, handleReject, isApproving, isRejecting } = useReviewApproval({
    documentId: documentRecord?.id,
    // Needed so the exclusion cascade can be resolved against the WHOLE
    // record set before approving, not just the loaded pages (#6848).
    transformationId: latestTransformation?.id,
    selectedClient,
    isClientMissing,
    originalClientId: documentRecord?.client_id_ref,
    excludedRecordIndices,
    reportedAsOf,
    // The rail's honoured acknowledgements, not the raw click map: a click
    // whose state has since changed is not in this list (!4936 review).
    acknowledgements: placedAcknowledgements,
    // Indices come from the paged record read, so a claimant's other rows may
    // be unfetched and the cascade needs server resolution unless the whole
    // set is loaded (#6848).
    exclusionScope: {
      source: "loaded-records",
      loadedRecordCount: loadedRecords.length,
      totalRecordCount: totalStoredRecords,
      totalRecordCountKnown: storedRecordCountKnown,
    },
  });

  // Document type detection — use mime type / file name since extraction_method is always "structured_parse"
  const mimeType = documentRecord?.object_metadata?.mime_type ?? "";
  const fileName = documentRecord?.name ?? "";
  const isPdfDoc = PDF_MIME_TYPES.has(mimeType) || PDF_EXTENSIONS.test(fileName);
  const isSpreadsheetDoc = !isPdfDoc; // If not a PDF, assume spreadsheet (existing behaviour)
  const hasSourcePreview = isSpreadsheetDoc || isPdfDoc;

  // Spreadsheet source preview — only fetches when drawer is open AND document is a spreadsheet
  const sourcePreview = useSourcePreview({
    documentId: documentRecord?.id,
    transformation: latestTransformation ?? null,
    enabled: sourcePreviewOpen && isSpreadsheetDoc,
    fieldMappings: template?.field_mappings,
    records: loadedRecords,
  });

  const sourcePreviewFocusRow = useMemo(() => {
    if (!sourcePreviewRecord) return null;
    return sourcePreview.getRowForRecord(sourcePreviewRecord);
  }, [sourcePreviewRecord, sourcePreview.getRowForRecord]);

  // PDF source preview — only fetches when drawer is open AND document is a PDF
  const pdfSourcePreview = usePdfSourcePreview({
    documentId: documentRecord?.id,
    transformation: latestTransformation ?? null,
    enabled: sourcePreviewOpen && isPdfDoc,
  });

  const pdfFocusPage = useMemo(() => {
    if (!sourcePreviewRecord || !isPdfDoc) return null;
    return pdfSourcePreview.getPageForRecord(sourcePreviewRecord);
  }, [sourcePreviewRecord, isPdfDoc, pdfSourcePreview.getPageForRecord]);

  // Build field-level bounding box highlights for the selected record (Phase 2)
  const pdfHighlights = useMemo(
    () => (isPdfDoc ? buildPdfHighlightsForRecord(sourcePreviewRecord) : []),
    [sourcePreviewRecord, isPdfDoc]
  );

  // Build columns dynamically from extracted data for the current schema tab
  const currentSchema = availableSchemas[selectedTab];
  const isTopSchema = !!grouping && currentSchema === grouping.topSchema;

  // Reimbursement records that will actually load on approve, derived from
  // the projection's survivor set. Computed once and reused in both tab-
  // label counts AND the grid's records prop so they can't drift.
  const displayedReimbursementRecords = useMemo(() => {
    const raw = (recordsBySchema[TargetSchema.REIMBURSEMENT] as ExtractedRecord[]) || [];
    if (!shouldApplyReimbursementSurvivorFilter) return raw;
    // Homogeneous reimbursement-schema list — every record should resolve to
    // an index, so an unknown one is anomalous and dropped.
    return filterReimbursementSurvivors(raw, { keepUnknown: false });
  }, [recordsBySchema, shouldApplyReimbursementSurvivorFilter, filterReimbursementSurvivors]);

  // REIMBURSEMENT-schema rendering — expand-row content, expand-label badge,
  // and the expandable-row set. Bundled in a hook so the schema-routing
  // decision lives at the call sites below while the body of each renderer
  // lives in one place. Operates on the raw bucketed reimbursement records
  // (not the survivor-filtered display list) so expandability and the
  // line-items badge match the projection's view of each row, regardless of
  // whether the row will ultimately survive load.
  const reimbursementRendering = useReimbursementSchemaRendering({
    records: (recordsBySchema[TargetSchema.REIMBURSEMENT] as ExtractedRecord[]) ?? [],
    getReimbursementRowForRecord,
  });

  // Records displayed for the current schema, with the reimbursement-survivor
  // filter applied when looking at REIMBURSEMENT on a reimbursement-record-type
  // doc. AB#4455 — the flat Reimbursements tab should mirror what approve will
  // actually create, not the raw extraction (which can include per-line claim
  // rows the loader drops and duplicate footer captures it collapses).
  // Whole-document errored records (#6853). Supersedes the summary
  // subtraction for the reviewer-facing surface: a bare off-page count can say
  // HOW MANY are unresolved but not WHERE or WHY, so it could only ever
  // justify the block, never resolve it. It is also what makes the approve
  // gate exclusion-aware — see unresolvedErrored below.
  //
  // Gated on the derived columns being trustworthy — the endpoint 409s
  // otherwise, correctly, and asking anyway would show an error state on every
  // pre-000220 document.
  const erroredRecords = useErroredRecords({
    documentId: documentRecord?.id ? String(documentRecord.id) : undefined,
    transformationId: latestTransformation?.id ? String(latestTransformation.id) : undefined,
    derivedColumnsAvailable: derivedColumnsAvailable(recordSummary),
    enabled: hasTransformation,
  });

  // Grid filter set by a rail card's "Show in grid" / "Show the N" (#7432
  // AC3/AC5). Null means no filter.
  //
  // Filtered by INDEX rather than by a boolean because the rail navigates per
  // REASON: "show me the 2 rows that have no claimant identity", not "show me
  // everything errored in this tab". `source` says where the rows come from:
  // errored rows live in the server's errored read (most are not loaded, so
  // the loaded set cannot show them), YTD-mismatch rows are loaded claimants.
  const [gridFilter, setGridFilter] = useState<{
    label: string;
    recordIndices: number[];
    source: "errored" | "loaded";
  } | null>(null);

  // Group pending an exclude confirmation (#6853 AC3).
  //
  // CONFIRMED, not immediate. Excluding a reason group can discard hundreds of
  // rows, and it is the RIGHT action only when those rows are genuinely junk —
  // if they error because one column is mis-mapped, this silently throws away
  // real claims data, which is the same class of silent decision #6921 exists
  // to surface. So the count and the reason are stated before it applies.
  const [pendingExcludeGroup, setPendingExcludeGroup] = useState<PendingExclude | null>(null);

  // How many records the confirm would actually exclude — net of what is
  // already excluded, so the dialog cannot promise more than the click does.
  const pendingExcludeCount = useMemo(
    () => (pendingExcludeGroup?.recordIndices ?? []).filter((index) => !excludedIndices.has(index)).length,
    [pendingExcludeGroup, excludedIndices]
  );

  const confirmExcludeGroup = useCallback(() => {
    const group = pendingExcludeGroup;
    setPendingExcludeGroup(null);
    if (!group) return;
    const wanted = new Set(group.recordIndices);
    // Errored groups come from the SERVER-fetched errored records, not the
    // loaded page: the whole point is that most of these are not loaded.
    // Outside-policy groups (#7436) are loaded claim lines. bulkExclude keys on
    // absolute record_index and cascades through linked records (#6848).
    const pool = group.source === "errored" ? erroredRecords.records : loadedRecords;
    const records = pool.filter((r) => typeof r.record_index === "number" && wanted.has(r.record_index));
    if (records.length > 0) handleBulkExclude(records, { cascade: group.cascade });
  }, [pendingExcludeGroup, erroredRecords.records, loadedRecords, handleBulkExclude]);

  const showInGrid = useCallback(
    (schema: TargetSchema, recordIndices: number[], label: string, source: "errored" | "loaded") => {
      const tabIndex = availableSchemas.indexOf(schema);
      // RETURNS EARLY on -1 rather than applying the filter anyway (!4682
      // review). A group can name a schema with no tab, if the only records
      // carrying it are the errored ones and they were filtered out of
      // availableSchemas. The guard was already here to avoid selecting tab -1
      // and blanking the grid — but the filter was then applied regardless,
      // which blanked the CURRENT tab instead. Doing nothing is worse than
      // navigating and better than silently emptying the grid the reviewer is
      // looking at.
      if (tabIndex < 0) return;
      setSelectedTab(tabIndex);
      setGridFilter({ label, recordIndices, source });
    },
    [availableSchemas]
  );

  // Changing tabs CLEARS the errored filter (!4682 review).
  //
  // Without this, one click from the new affordance reproduces the dead-end it
  // was built to remove: after "Show in Claims", clicking Claimants leaves the
  // index filter active, the schema predicate matches nothing, and the
  // reviewer lands on "Showing 0 errored records" on an 8,000-row document
  // with the tab label still showing its unfiltered count. The chip's clear
  // is the escape, so it was recoverable — but it should not be reachable.
  //
  // Clearing rather than re-filtering because the filter is a set of indices
  // the reviewer asked to see in ONE schema; carrying it to a different tab
  // would be answering a question they did not ask.
  const selectTab = useCallback((tabIndex: number) => {
    setSelectedTab(tabIndex);
    setGridFilter(null);
  }, []);

  // Also cleared when the transformation changes, for the same reason
  // useRecordExclusions resets its set: these are ABSOLUTE record indices, and
  // index N in a reprocessed run is a different record. A reprocess-in-place
  // swaps the transformation without remounting this tab, so a carried-over
  // filter would show plausible rows that are not the ones the reviewer asked
  // for — and it would do it silently.
  useEffect(() => {
    setGridFilter(null);
  }, [latestTransformation?.id]);

  const displayedRecordsForCurrentSchema = useMemo(() => {
    // An errored-group filter REPLACES the record source rather than filtering
    // it (#6853). This is the load-bearing detail of the whole feature.
    //
    // The banner navigates to records at indices like 5500 and 6700 while the
    // grid holds 0-999, so filtering the LOADED set by those indices renders an
    // empty grid — the stepper's failure mode with a different cause. These
    // records came from the server's ?errored=true read, so they are in hand
    // regardless of how far the reviewer has paged.
    if (gridFilter) {
      const wanted = new Set(gridFilter.recordIndices);
      const pool = gridFilter.source === "errored" ? erroredRecords.records : loadedRecords;
      return pool.filter(
        (r) =>
          typeof r.record_index === "number" &&
          wanted.has(r.record_index) &&
          getRecordSchemas(r).includes(currentSchema)
      );
    }
    if (currentSchema === TargetSchema.REIMBURSEMENT) {
      return displayedReimbursementRecords;
    }
    return (recordsBySchema[currentSchema] as ExtractedRecord[]) || [];
  }, [
    recordsBySchema,
    currentSchema,
    displayedReimbursementRecords,
    gridFilter,
    erroredRecords.records,
    loadedRecords,
  ]);

  // Per-schema record counts, with the reimbursement-survivor filter folded in
  // so the tab labels match what's actually shown in the grid. Other schemas
  // pass through unchanged.
  const displayedRecordCounts = useMemo(() => {
    if (!shouldApplyReimbursementSurvivorFilter) return recordCounts;
    return {
      ...recordCounts,
      [TargetSchema.REIMBURSEMENT]: displayedReimbursementRecords.length,
    };
  }, [recordCounts, shouldApplyReimbursementSurvivorFilter, displayedReimbursementRecords.length]);

  const fullColumns = useMemo(
    () => buildDynamicColumns(displayedRecordsForCurrentSchema, currentSchema, { showAllColumns }),
    [displayedRecordsForCurrentSchema, currentSchema, showAllColumns]
  );

  // Top-row schema shows a limited set of priority columns inline; remaining
  // fields render inside `expandRow` via ExpandedRecordDetails.
  // Redesigned claimant grid (#7435, artboard 2a): the top row carries the
  // name only — relationship and claim-line count go in the sub-line, Member
  // ID / DOB / the rest in the expanded panel's fact line. Column selection
  // by position, not field name, so a file that maps the ID under another
  // name cannot leak it back into the row.
  const isClaimLinesTab = currentSchema === TargetSchema.CLAIM_ITEM;
  const isClaimantGrid =
    isTopSchema &&
    currentSchema === TargetSchema.CLAIMANT &&
    documentRecord?.record_type !== DocumentRecordType.REIMBURSEMENTS;
  const displayedColumns = useMemo(() => {
    if (isClaimantGrid) {
      const name = selectClaimantNameColumn(fullColumns);
      return name ? [name] : [];
    }
    if (!isTopSchema || fullColumns.length <= TOP_ROW_PRIORITY_COLUMNS) return fullColumns;
    return fullColumns.slice(0, TOP_ROW_PRIORITY_COLUMNS);
  }, [fullColumns, isTopSchema, isClaimantGrid]);

  // Keys already rendered as columns — passed to ExpandedRecordDetails so it
  // only renders non-priority fields as key/value overflow.
  const shownFieldKeys = useMemo(() => {
    const set = new Set<string>();
    displayedColumns.forEach((c: CompactTableColumn<ExtractedRecord>) => {
      // Drop the __xref_ prefix used for cross-reference columns so the real
      // field name (e.g. claimant_name) still counts as shown.
      const key = c.key.startsWith("__xref_") ? c.key.slice("__xref_".length) : c.key;
      set.add(key);
    });
    return set;
  }, [displayedColumns]);

  const handleOpenSourcePreview = useCallback((record: ExtractedRecord) => {
    setSourcePreviewRecord(record);
    onSourcePreviewChange(true);
  }, []);

  // Per-schema record-issue summary: severity for tab badges + global counts
  // for approve-button gating. We don't render individual issues anywhere
  // (the issues panel is gone), so we only collect what the surface uses.
  const { errorCount, warningCount, issueSeverityBySchema } = useMemo(() => {
    const severity: Partial<Record<TargetSchema, RecordIssueSeverity>> = {};
    let errors = 0;
    let warnings = 0;

    const promote = (schema: TargetSchema, level: RecordIssueSeverity) => {
      // Tab icon shows the highest severity per schema. "error" beats
      // "warning"; once a schema is at "error" no further writes change it.
      if (severity[schema] !== "error") severity[schema] = level;
    };

    for (const schema of availableSchemas) {
      const records = recordsBySchema[schema] || [];
      for (const record of records) {
        // Excluded records don't block approval, so their issues don't count.
        if (isRecordExcluded(record)) continue;

        const validationErrors = getRecordValidationErrors(record).filter((e) => !e.schema || e.schema === schema);
        const transformationErrors = getRecordTransformationErrors(record).filter(
          (e) => !e.schema || e.schema === schema
        );

        if (validationErrors.length > 0) {
          errors += validationErrors.length;
          promote(schema, "error");
        }
        if (transformationErrors.length > 0) {
          warnings += transformationErrors.length;
          promote(schema, "warning");
        }
      }
    }

    return { errorCount: errors, warningCount: warnings, issueSeverityBySchema: severity };
  }, [availableSchemas, recordsBySchema, isRecordExcluded]);

  // Whole-document errored records the reviewer has NOT resolved (#6853,
  // !4682 review).
  //
  // THIS REPLACES AN EXCLUSION-BLIND SUBTRACTION, and the reason it had to is
  // that this PR falsified the invariant it rested on. It was
  //
  //     record_summary.records_with_errors − summarizeRecords(loaded)
  //
  // justified by: "a reviewer also cannot exclude a record they have never
  // been shown, so exclusions can only ever apply to the loaded side." True
  // until the errored banner shipped. Now excluding an off-page reason group
  // writes real indices into the exclusion set, and neither term above moves —
  // so on exactly the document #6853 exists for (errors at 5,500 and 6,700,
  // none loaded) the reviewer excludes them, nothing changes, and Approve
  // stays disabled with no way forward.
  //
  // The fix is to count from the SERVER'S ERRORED READ instead. Those records
  // carry absolute record_index, which is what an exclusion is expressed in,
  // so exclusions can actually be subtracted. The summary subtraction survives
  // only as the fallback for when that read is unavailable.
  // Errored records the SERVER reported that the reviewer has not excluded.
  // See utils/erroredGating for why this arithmetic lives outside the
  // component: it was a memo here, it was wrong, and nothing rendered this
  // component so nothing could catch it.
  const serverUnresolved = useMemo(
    () => unresolvedErroredFromServer(erroredRecords.total, erroredRecords.records, excludedIndices),
    [erroredRecords.total, erroredRecords.records, excludedIndices]
  );

  // What the APPROVE GATE blocks on. The banner is deliberately NOT given this
  // number — it can name reasons and offer actions only for records the errored
  // read returned, and a header count it could neither break down nor act on
  // would be a dead end.
  const unresolvedErrored = useMemo(
    () =>
      unresolvedErroredForGate({
        serverTotal: erroredRecords.total,
        serverUnresolved,
        serverTruncated: erroredRecords.isTruncated,
        summaryErroredTotal: recordSummary?.records_with_errors,
        loadedErroredCount: summarizeRecords(loadedRecords).records_with_errors,
        hasMoreRecords,
      }),
    [erroredRecords.total, erroredRecords.isTruncated, serverUnresolved, recordSummary, loadedRecords, hasMoreRecords]
  );

  // The rail is always there (#7432): "All checks pass" is a verdict the
  // reviewer should see, not an absence. Compare with source collapses it
  // (#7431 AC3); the sticky bar below takes over its job while it is hidden.
  const completeness = latestTransformation?.processing_metadata?.completeness;
  const showSidebar = !sourcePreviewOpen;

  // Paid-through (as-of) state — derived ONCE here (ADR-045 2026-07-28
  // amendment §2; ADR-051 §8 local-date rule) and shared by the header bar and
  // the sticky approve bar, so a next-day as-of cannot be blocked in one and
  // "All checks pass" in the other.
  const asOfState = useReviewAsOfState({
    recordType: documentRecord?.record_type,
    extractedRecords: loadedRecords,
    reviewProjection,
    reportedAsOf,
    recordSummary,
    recordsPartial: !!hasMoreRecords,
  });

  // "Checking…" while the approval forecast is still loading for a resolved
  // client (#7431 AC2). A missing client is its own, earlier cause.
  const isProjectionChecking = isProjectionLoading && !isClientMissing;

  // ── The one list of checks (#7432) ─────────────────────────────────────
  const claimantRecords = useMemo(
    () => (recordsBySchema[TargetSchema.CLAIMANT] as ExtractedRecord[] | undefined) ?? [],
    [recordsBySchema]
  );
  const claimLineRecords = useMemo(
    () => (recordsBySchema[TargetSchema.CLAIM_ITEM] as ExtractedRecord[] | undefined) ?? [],
    [recordsBySchema]
  );
  const isReimbursementDoc = documentRecord?.record_type === DocumentRecordType.REIMBURSEMENTS;

  // Reported YTD vs claim lines (#7432 AC5) — over the LOADED records; the
  // rail's scope note says so on a paged document.
  const ytd = useMemo(
    () =>
      isReimbursementDoc
        ? null
        : reconcileReportedYtd({ claimants: claimantRecords, linesOf: getLinkedRecords, isExcluded: isRecordExcluded }),
    [isReimbursementDoc, claimantRecords, getLinkedRecords, isRecordExcluded]
  );
  const ytdMismatchByIndex = useMemo(() => {
    const m = new Map<number, YtdMismatch>();
    ytd?.mismatches.forEach((x) => m.set(x.recordIndex, x));
    return m;
  }, [ytd]);
  const showYtdMismatches = useCallback(() => {
    if (!ytd) return;
    showInGrid(
      TargetSchema.CLAIMANT,
      ytd.mismatches.map((x) => x.recordIndex),
      `Reported YTD ≠ claim lines (${ytd.mismatches.length.toLocaleString()})`,
      "loaded"
    );
  }, [ytd, showInGrid]);

  // Errored reason groups net of exclusions, each with its Show / Exclude.
  const erroredGroupInputs = useMemo<ErroredReasonGroupInput[]>(
    () =>
      erroredRecords.groups
        .map((group) => {
          const remaining = group.recordIndices.filter((index) => !excludedIndices.has(index));
          const kept = new Set(remaining);
          const schemas = (Object.entries(group.recordIndicesBySchema) as [TargetSchema, number[]][])
            .map(([schema, indices]) => [schema, indices.filter((i) => kept.has(i)).length] as [TargetSchema, number])
            .filter(([, n]) => n > 0)
            .sort((a, b) => b[1] - a[1])
            .map(([schema]) => schema);
          return {
            rule: group.rule,
            label: group.label,
            remaining,
            schemas,
            onShow: (schema: TargetSchema, indices: number[]) =>
              showInGrid(schema, indices, `${group.label} (${indices.length.toLocaleString()})`, "errored"),
            onExclude: () =>
              setPendingExcludeGroup({
                label: group.label,
                recordIndices: group.recordIndices,
                source: "errored",
                why: "These rows will not load. If they are erroring because a column is mis-mapped, adjust the field mapping instead — excluding them discards the data.",
                // A claimant group takes its lines; a claim-line group is those lines only.
                cascade: schemas[0] !== TargetSchema.CLAIM_ITEM,
              }),
          };
        })
        .filter((g) => g.remaining.length > 0),
    [erroredRecords.groups, excludedIndices, showInGrid]
  );

  // A CLAIMANT is excluded when the reviewer excluded it, not merely one of its
  // lines. In a row-per-line file the claimant's top row is also its first
  // claim line, so `isRecordExcluded(topRow)` answers for that one line —
  // excluding it from the Claims tab or the rail struck the whole claimant
  // and counted it as "1 claimant excluded" while six lines still loaded
  // (#7436 walkthrough). The claimant reads as excluded only when every one
  // of its loaded claim lines is (a claimant with no lines: when its row is).
  const isClaimantExcluded = useCallback(
    (record: ExtractedRecord): boolean => {
      if (!isRecordExcluded(record)) return false;
      const lines = getLinkedRecords(record).filter((r) =>
        (r.extracted_fields ?? []).some((f) => f.target_schema === TargetSchema.CLAIM_ITEM)
      );
      return lines.every(isRecordExcluded);
    },
    [isRecordExcluded, getLinkedRecords]
  );
  const excludedClaimantCount = useMemo(
    () => claimantRecords.filter(isClaimantExcluded).length,
    [claimantRecords, isClaimantExcluded]
  );
  const excludedLineCount = useMemo(
    () => claimLineRecords.filter(isRecordExcluded).length,
    [claimLineRecords, isRecordExcluded]
  );
  const restoreAllExclusions = useCallback(() => {
    const excluded = loadedRecords.filter(isRecordExcluded);
    if (excluded.length > 0) handleBulkRestore(excluded);
  }, [loadedRecords, isRecordExcluded, handleBulkRestore]);

  // "Change date" on the paid-through card opens the header's own control —
  // there is one as-of control on this screen, and it lives in the header.
  const focusAsOfControl = useCallback(() => {
    const trigger = document.querySelector<HTMLElement>('[aria-label="Change paid-through date"]');
    trigger?.scrollIntoView({ block: "center", behavior: "smooth" });
    trigger?.click();
  }, []);

  const canFix = !isViewOnly;

  // Where the paid-through date came from (#7439 AC1/AC4). "defaulted" is the
  // server's prior-month-end guess, still untouched: the rail asks the
  // reviewer to confirm it. The moment they change the date it is theirs.
  const asOfSource: "reviewer" | "file" | "defaulted" | "none" = asOfTouched
    ? "reviewer"
    : !reportedAsOf
      ? "none"
      : reviewProjection?.default_reported_as_of_source === "prior_month_end" &&
          reportedAsOf === reviewProjection.default_reported_as_of
        ? "defaulted"
        : "file";

  const fixDisabledReason = !hasTransformation
    ? "Available once the document has a template and field mappings."
    : isClientMissing
      ? "Select a client first."
      : undefined;
  const openFieldMapping = canFix && !fixDisabledReason ? () => setMappingDialogOpen(true) : undefined;

  // Claim lines outside the policy dates (#7436): one group per failed check,
  // built from the loaded lines' forecasts so Show in grid and Exclude N act
  // on real records and the counts fall as lines are excluded. The server's
  // whole-set groups back the figures on a paged document.
  const outsidePolicyGroups = useMemo(
    () =>
      buildOutsidePolicyGroups({
        records: claimLineRecords,
        forecastFor: getClaimItemRowForRecord,
        isExcluded: isRecordExcluded,
        windows: reviewProjection?.policy_windows,
        serverGroups:
          ((reviewProjection?.warnings ?? []).find((w) => w.kind === OUTSIDE_POLICY_KIND)?.details?.groups as
            | Array<{ reason: string; count: number; amount: string }>
            | undefined) ?? null,
      }),
    [
      claimLineRecords,
      getClaimItemRowForRecord,
      isRecordExcluded,
      reviewProjection?.policy_windows,
      reviewProjection?.warnings,
    ]
  );
  const outsidePolicyInput = useMemo(
    () => ({
      windowsLabel: policyWindowsLabel(reviewProjection?.policy_windows),
      engine: reviewProjection?.policy_windows?.engine ?? null,
      groups: outsidePolicyGroups.map((g) => ({
        key: g.reason,
        label: g.label,
        count: g.count,
        amount: g.amount,
        loadedCount: g.recordIndices.length,
        onShow:
          g.recordIndices.length > 0
            ? () =>
                showInGrid(
                  TargetSchema.CLAIM_ITEM,
                  g.recordIndices,
                  `${g.label} (${g.recordIndices.length.toLocaleString()})`,
                  "loaded"
                )
            : undefined,
        onExclude:
          g.recordIndices.length > 0
            ? () =>
                setPendingExcludeGroup({
                  label: g.label,
                  recordIndices: g.recordIndices,
                  source: "loaded",
                  why: "These claim lines will not load. If the dates are right and the file belongs to another policy term, load it against that policy instead — excluding them discards the data.",
                  cascade: false,
                })
            : undefined,
      })),
    }),
    [outsidePolicyGroups, reviewProjection?.policy_windows, showInGrid]
  );

  // Paid amount / paid date mapping (#7380): the template says what is mapped,
  // the coverage analysis how many lines were empty, the server's per-schema
  // count (or the loaded count on a fully loaded file) how many lines there are.
  const paidDateCoverage = useMemo(
    () =>
      isReimbursementDoc
        ? null
        : assessPaidDateCoverage({
            mappedFields: (template?.field_mappings ?? []).map((m) => ({
              schema: m.target_schema,
              field: m.target_field,
            })),
            coverage: latestTransformation?.coverage_analysis,
            lineCount:
              recordSummary?.counts_by_schema?.[TargetSchema.CLAIM_ITEM] ??
              (hasMoreRecords ? null : claimLineRecords.length),
          }),
    [
      isReimbursementDoc,
      template?.field_mappings,
      latestTransformation?.coverage_analysis,
      recordSummary,
      hasMoreRecords,
      claimLineRecords.length,
    ]
  );

  const reviewChecks = useMemo(
    () =>
      buildReviewChecks({
        canFix,
        acknowledged: acknowledgements.acknowledged,
        onUndo: acknowledgements.undo,
        mapping: {
          blockingIssues: mappingValidationIssues,
          requiredGaps: requiredAdvisory,
          transformationWarningCount: warningCount,
          onOpenFieldMapping: openFieldMapping,
        },
        rows: {
          unresolvedCount: unresolvedErrored.count,
          unresolvedCountIsExact: unresolvedErrored.exact,
          loadedErrorCount: errorCount,
          groups: erroredGroupInputs,
          notChecked: hasTransformation && (!derivedColumnsAvailable(recordSummary) || erroredRecords.unavailable),
          isLoading: erroredRecords.isLoading,
          readFailed: !!erroredRecords.error,
          schemaLabel: (schema) => SCHEMA_LABELS[schema] || schema,
        },
        ytd: ytd ? { ...ytd, onShow: showYtdMismatches } : null,
        asOf: {
          show: asOfState.showAsOf,
          label: reportedAsOf ? formatDate(reportedAsOf, DATE_FORMATS.DISPLAY_LONG) : null,
          isInFuture: asOfState.isInFuture,
          isStale: asOfState.isStale,
          isMissingForYtd: asOfState.showAsOf && asOfState.hasMappedYtd && !reportedAsOf,
          latestClaimPaidLabel: asOfState.latestClaimPaidDate
            ? formatDate(asOfState.latestClaimPaidDate, DATE_FORMATS.DISPLAY_LONG)
            : null,
          latestPaidCheckPartial: asOfState.latestPaidCheckPartial,
          source: asOfSource,
          onChange: canFix && !asOfState.isDisabled ? focusAsOfControl : undefined,
        },
        advisories: reviewProjection?.warnings ?? [],
        onCompareSource: hasSourcePreview ? () => onSourcePreviewChange(true) : undefined,
        outsidePolicy: outsidePolicyInput,
        paidDate: paidDateCoverage,
        // Flag off → the legacy stamp never writes missing_paid_date, so the
        // pre-filtered list would be empty: no link, no facet copy.
        claimLinesWithoutPaidDateHref:
          canListClaimLines && featureFlags.deductibleBasisViewEnabled
            ? claimItemsByReasonPath(["missing_paid_date"], resolvedClientId ? { client_id_ref: resolvedClientId } : {})
            : null,
        completeness,
        doubleCountWarningMessage: asOfState.doubleCountWarningMessage,
        exclusions: { claimants: excludedClaimantCount, lines: excludedLineCount, onRestoreAll: restoreAllExclusions },
        counts: {
          loadedClaimants: claimantRecords.length,
          totalClaimants: recordSummary?.counts_by_schema?.[TargetSchema.CLAIMANT] ?? null,
          loadedLines: claimLineRecords.length,
          partial: !!hasMoreRecords,
        },
        layoutLabel: template?.name ?? null,
        rowDecisions: selectRowDecisions(latestTransformation),
        isProjectionChecking,
      }),
    [
      canFix,
      acknowledgements.acknowledged,
      acknowledgements.undo,
      mappingValidationIssues,
      requiredAdvisory,
      warningCount,
      openFieldMapping,
      unresolvedErrored.count,
      unresolvedErrored.exact,
      errorCount,
      erroredGroupInputs,
      hasTransformation,
      recordSummary,
      erroredRecords.unavailable,
      erroredRecords.isLoading,
      erroredRecords.error,
      ytd,
      showYtdMismatches,
      asOfState,
      reportedAsOf,
      asOfSource,
      focusAsOfControl,
      reviewProjection?.warnings,
      hasSourcePreview,
      outsidePolicyInput,
      paidDateCoverage,
      canListClaimLines,
      resolvedClientId,
      onSourcePreviewChange,
      completeness,
      excludedClaimantCount,
      excludedLineCount,
      restoreAllExclusions,
      claimantRecords.length,
      claimLineRecords.length,
      hasMoreRecords,
      template?.name,
      latestTransformation,
      isProjectionChecking,
    ]
  );
  // The rail's "Dates are right" for the outside-policy card has been
  // honoured this render (#7436 AC3): flagged lines drop the warning tone.
  const outsidePolicyAcknowledged = reviewChecks.passLines.some((p) => p.key === advisoryCheckKey(OUTSIDE_POLICY_KIND));

  // The header's Paid through button mirrors the rail (#7439 AC1): warning
  // tone and icon while a paid-through card is showing, cleared the moment
  // the reviewer confirms or changes the date. A future date is the error
  // tone on its own.
  const asOfFlagged = reviewChecks.cards.some((c) => c.key === CHECK_PAID_THROUGH && c.severity === "advisory");

  // Publish the honoured acknowledgements for the approval hook and the dialog.
  // Compared by value so a same-content render does not loop.
  useEffect(() => {
    setPlacedAcknowledgements((prev) =>
      prev.length === reviewChecks.acknowledgements.length &&
      prev.every(
        (a, i) =>
          a.check === reviewChecks.acknowledgements[i].check && a.detail === reviewChecks.acknowledgements[i].detail
      )
        ? prev
        : reviewChecks.acknowledgements
    );
  }, [reviewChecks.acknowledgements]);

  // ONE gate for the rail's Approve and the sticky bar's Approve: the rail's
  // attention count, plus the causes that come before any check (access,
  // client, nothing to load). `isReadyForApproval` folds in the caller-side
  // gates the rail does not name.
  const approveStatus = deriveApproveStatus({
    readOnly: !permissionsLoading && !canCreate,
    partnerReadOnly: !!partner?.readOnly,
    isClientMissing,
    isChecking: reviewChecks.isChecking,
    checksDone: reviewChecks.checksDone,
    checksTotal: reviewChecks.checksTotal,
    errorCount,
    unresolvedErroredCount: unresolvedErrored.count,
    mappingBlockingCount: mappingValidationIssues.length,
    isAsOfInFuture: asOfState.isInFuture,
    attention: { count: reviewChecks.attentionCount, names: reviewChecks.attentionNames },
  });
  const approveBlocked = approveStatus.blocked || !isReadyForApproval;
  const openApproveDialog = useCallback(() => setApproveDialogOpen(true), []);

  // Approve confirm content (#7440 AC1–AC5). The policy is the projection's
  // resolved_policy (#7462) — the same label the header shows. When the
  // projection is skipped, refused or failed, the header and the confirm fall
  // back together to the client's newest policy (!4938 review).
  const projectionUnavailable = isProjectionSkipped || !!projectionError;
  const fallbackPolicyLabel = useFallbackPolicyLabel(
    resolvedClientId ? String(resolvedClientId) : undefined,
    projectionUnavailable && !reviewProjection?.resolved_policy
  );
  const policyLabel = resolvedPolicyLabel(reviewProjection?.resolved_policy) ?? fallbackPolicyLabel;
  const categoryLabel = documentClassification?.effective || documentClassification?.auto_classified || null;
  const approveSubtitle = [
    selectedClient?.client_legal_name ?? selectedClient?.client_short_name ?? null,
    policyLabel,
    categoryLabel,
  ]
    .filter(Boolean)
    .join(" · ");
  const approveSummary = useMemo(
    () =>
      buildApproveSummary({
        claimants: claimantRecords,
        linesOf: getLinkedRecords,
        isExcluded: isRecordExcluded,
        projectionFor: getProjectionForRecord,
        partial: !!hasMoreRecords || isProjectionWindowed,
        totalClaimants: recordSummary?.counts_by_schema?.[TargetSchema.CLAIMANT] ?? null,
        rowForecastFor: getClaimItemRowForRecord,
        isClaimantExcluded,
        engine: reviewProjection?.policy_windows?.engine ?? null,
      }),
    [
      claimantRecords,
      getLinkedRecords,
      isRecordExcluded,
      isClaimantExcluded,
      getClaimItemRowForRecord,
      getProjectionForRecord,
      hasMoreRecords,
      isProjectionWindowed,
      recordSummary,
      reviewProjection?.policy_windows?.engine,
    ]
  );
  const mappingRecap = useMemo(() => buildMappingRecap(template?.field_mappings), [template?.field_mappings]);
  // The errored read and the mapping are whole-document; only the reconciliation
  // and the grid-derived figures are bounded by what is loaded.
  const railScopeNote = hasMoreRecords
    ? `The YTD check covers the ${loadedRecords.length.toLocaleString()} loaded records. Mapping and row checks cover the whole file.`
    : reviewProjection?.family_rollup_unavailable
      ? "Family totals and stop-loss outcomes aren't shown for a partial file. They're evaluated when the file loads."
      : null;

  // Claimant-grid context (#7435): claim-line count for the sub-line and the
  // paid-through date for the YTD header. The count matches over LOADED
  // records, the same scope as the expand badge.
  const claimantGridContext = useMemo(
    () => ({
      lineCount: (record: ExtractedRecord) =>
        getLinkedRecords(record).filter((r) =>
          (r.extracted_fields ?? []).some((f) => f.target_schema === TargetSchema.CLAIM_ITEM)
        ).length,
      // Loaded-set count is a partial answer on a paged document (ADR-045
      // 2026-09-08 amendment §3) — render "N+" rather than a confident N.
      lineCountPartial: !!hasMoreRecords,
      asOfLabel: reportedAsOf ? formatDate(reportedAsOf, DATE_FORMATS.DISPLAY_LONG) : undefined,
      // The Deductible header tooltip names the same policy as the header's
      // Policy cell (#7462 AC2) — "this policy" meant nothing in particular.
      policyLabel: policyLabel ?? undefined,
      // Reported-YTD vs claim-lines gap, per row (#7432 AC5).
      ytdMismatchFor: (record: ExtractedRecord) => {
        const idx = absoluteIndexOf(record);
        return idx === undefined ? undefined : ytdMismatchByIndex.get(idx);
      },
      // The claimant's claim lines in THIS file that will insert on approve
      // (not excluded, not already on file) — the open-claim preview shows
      // them as incoming rows beside what is on the claim.
      incomingLinesFor: (record: ExtractedRecord): IncomingClaimLine[] =>
        getLinkedRecords(record)
          .filter(
            (r) =>
              !isRecordExcluded(r) &&
              (r.extracted_fields ?? []).some((f) => f.target_schema === TargetSchema.CLAIM_ITEM) &&
              !getClaimItemRowForRecord(r)?.is_duplicate_on_file
          )
          .map((r) => {
            const d = getRecordData(r, TargetSchema.CLAIM_ITEM);
            const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
            return {
              serviceDate: str(d.date_of_service_start) ?? str(d.date_of_service),
              paidDate: str(d.paid_date),
              procedureCode: str(d.procedure_code),
              procedureDescription: str(d.procedure_description),
              paidAmount: parseMoneyCell(d.paid_amount) ?? 0,
            };
          }),
    }),
    [
      getLinkedRecords,
      isRecordExcluded,
      getClaimItemRowForRecord,
      reportedAsOf,
      policyLabel,
      hasMoreRecords,
      ytdMismatchByIndex,
    ]
  );

  // Suppress the expand chevron for rows whose panel would be empty. Each row's
  // expandability requires an O(M) scan via getLinkedRecords; called inline per
  // row that's O(N×M) on every render. Precompute the result set once per
  // dependency change and do O(1) Set lookups in the per-row callback so search
  // keypresses and tab switches don't redo the work. Defined here (before the
  // early returns below) to satisfy rules-of-hooks.
  //
  // Reimbursement schema (flat tab): expandability is "row has line_items".
  // Top schema (Claimants tab): expandability is the existing nested-schemas
  // check via hasExpandableContent.
  const expandableRowSet = useMemo(() => {
    if (currentSchema === TargetSchema.REIMBURSEMENT) return reimbursementRendering.expandableRowSet;
    const set = new Set<number | ExtractedRecord>();
    if (!isTopSchema || !grouping) return set;
    // BUILT FROM THE DISPLAYED RECORDS, not from recordsBySchema (!4682
    // review). Under the errored filter the grid is fed
    // erroredRecords.records — fresh objects from the server read — while this
    // set was built from the loaded page. Nothing matched, so every errored
    // row lost its chevron, and LinkedRecordsDisclosure (added in this same
    // PR) was unreachable from the view it exists for. Not scale-dependent:
    // it was true on a fully loaded document too.
    for (const r of displayedRecordsForCurrentSchema) {
      if (hasExpandableContent(r, currentSchema, shownFieldKeys, getLinkedRecords(r), grouping.nestedSchemas)) {
        set.add(expandableRowKey(r));
      }
    }
    return set;
  }, [
    currentSchema,
    reimbursementRendering.expandableRowSet,
    isTopSchema,
    grouping,
    displayedRecordsForCurrentSchema,
    shownFieldKeys,
    getLinkedRecords,
  ]);

  const isRowExpandable = useCallback(
    (record: ExtractedRecord): boolean => expandableRowSet.has(expandableRowKey(record)),
    [expandableRowSet]
  );

  if (!documentRecord) {
    return <Box sx={{ p: 3 }}>No document record found</Box>;
  }

  // Wait for the records too, not just the transformation (#6755). Records are
  // a second request now, and recordsReady stays false through an auto-loading
  // document's whole page sequence — otherwise the grid would render a
  // half-loaded set and then grow under the reviewer.
  if (isLoading || (hasTransformation && !recordsReady && !recordsError)) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 8 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          {totalStoredRecords > REVIEW_AUTOLOAD_MAX_RECORDS
            ? `Loading ${totalStoredRecords.toLocaleString()} records...`
            : "Loading document records..."}
        </Typography>
      </Box>
    );
  }

  if (recordsError) {
    return (
      <Box sx={{ px: 2.5, py: 4 }}>
        <Alert severity="error">
          Could not load this document&apos;s extracted records. Refresh to try again — the document itself is
          unaffected.
        </Alert>
      </Box>
    );
  }

  if (!hasRecords) {
    return (
      <Box sx={{ textAlign: "center", py: 8, color: "text.secondary" }}>
        <Typography variant="body1">No proposed changes to review</Typography>
      </Box>
    );
  }

  // Linked records per top-row record — drives expand badge count and nested tables.
  // For reimbursement-record-type docs we hide records the loader will drop or
  // collapse (per-line claim rows, duplicate footer captures), so the nested
  // reimbursements table shows ONLY the rows that will actually become
  // reimbursements on approve. AB#4455.
  const renderExpandRow = (record: ExtractedRecord) => {
    if (currentSchema === TargetSchema.REIMBURSEMENT) return reimbursementRendering.renderExpandRow(record);
    if (!isTopSchema || !grouping) return null;
    let linked = getLinkedRecords(record);
    if (shouldApplyReimbursementSurvivorFilter) {
      linked = filterReimbursementSurvivors(linked);
    }
    return (
      <>
        <ExpandedRecordDetails
          record={record}
          parentSchema={currentSchema}
          shownFieldKeys={shownFieldKeys}
          linkedRecords={linked}
          nestedSchemas={grouping.nestedSchemas}
          onOpenFieldMapping={() => setMappingDialogOpen(true)}
          isFieldMappingDisabled={!hasTransformation || isClientMissing}
          getReimbursementRowForRecord={getReimbursementRowForRecord}
          getClaimItemRowForRecord={getClaimItemRowForRecord}
          showAllColumns={showAllColumns}
          policyWindows={reviewProjection?.policy_windows}
          outsidePolicyAcknowledged={outsidePolicyAcknowledged}
          isExcluded={isRecordExcluded}
          claimantProjection={isClaimantGrid ? getProjectionForRecord(record) : undefined}
          onViewSource={hasSourcePreview ? handleOpenSourcePreview : undefined}
          sourceType={isPdfDoc ? "pdf" : "spreadsheet"}
        />
        {/* getLinkedRecords matches over LOADED records, so on a paged
            document this panel shows a subset and looked complete (#6900).
            Measured: claimant M0000 has 7 rows at indices 0, 1200 ... 7200 and
            the panel showed 1. Mounts only on expand, so this is one request
            per expand on an explicit action -- and none at all when everything
            is loaded. */}
        <LinkedRecordsDisclosure
          documentId={documentRecord?.id ? String(documentRecord.id) : undefined}
          transformationId={latestTransformation?.id ? String(latestTransformation.id) : undefined}
          recordIndex={typeof record.record_index === "number" ? record.record_index : undefined}
          shownCount={linked.length}
          hasMoreRecords={hasMoreRecords}
        />
      </>
    );
  };

  const renderExpandLabel = (record: ExtractedRecord) => {
    if (currentSchema === TargetSchema.REIMBURSEMENT) return reimbursementRendering.renderExpandLabel(record);
    if (!isTopSchema || !grouping) return null;
    // Claimant grid (#7435): the name's sub-line says "N claim lines", so the
    // chevron's count badge would be the same number a second time.
    if (isClaimantGrid) return null;
    const nested = new Set(grouping.nestedSchemas);
    const linked = getLinkedRecords(record).filter((r) =>
      (r.extracted_fields ?? []).some((f) => nested.has(f.target_schema))
    );
    // Mirror the survivor filter applied to the nested table so the badge
    // count matches what the reviewer will actually see on expand. Without
    // this, the chevron says "11" but expanding shows 1 — confusing. AB#4455.
    const filtered = shouldApplyReimbursementSurvivorFilter ? filterReimbursementSurvivors(linked) : linked;
    const count = filtered.length;
    if (count === 0) return null;
    return (
      <Chip label={count} size="small" sx={{ height: 18, fontSize: "0.7rem", "& .MuiChip-label": { px: 0.75 } }} />
    );
  };

  return (
    // width:100% + minWidth:0 anchor the entire tab to the page width instead
    // of letting the inner table push the whole layout wider than the viewport.
    // Without these the maxWidth:100% on the flex container below resolves
    // against its content-sized parent — circular sizing — and the sidebar
    // ends up scrolled off the right edge of the page.
    <Box sx={{ width: "100%", minWidth: 0 }}>
      {/* Two-column layout: main column (gate + toolbar + grid) + sidebar.
          Using CSS grid with minmax(0, 1fr) for the main column — that's the
          deterministic way to say "this column never exceeds 1fr of available
          space, regardless of content min-content-size." Flex's flex:1 +
          minWidth:0 is supposed to do the same, but browsers can still let
          flex children grow to their min-content-size when descendants have
          intrinsic widths (a wide table here). Grid's minmax(0, 1fr)
          enforces the cap unconditionally so a wide table inside scrolls
          rather than pushing the sidebar off the page. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: showSidebar ? "minmax(0, 1fr) 360px" : "minmax(0, 1fr)",
          gap: 2.5,
          alignItems: "flex-start",
          maxWidth: "100%",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {/* Client gate — top of main column. Grid visually dims below when missing. */}
          <ClientGate
            requiresClient={requiresClient}
            isClientMissing={isClientMissing}
            selectedClient={selectedClient}
            onClientChange={setSelectedClient}
            onRejectRequest={isViewOnly ? undefined : () => setRejectDialogOpen(true)}
            showAssignedState={!featureFlags.documentReviewRedesignEnabled}
          />

          {/* Projection-failure banner — non-blocking. Grid still renders; chips
              fall back to "—" when the forecast is unavailable. */}
          {projectionError && !isClientMissing && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Couldn&apos;t load the approval forecast: {projectionError}. The grid still reflects extracted data.
            </Alert>
          )}

          {/* Review context bar (#6099, behind featureFlags.documentReviewRedesignEnabled)
              re-presents the same context — detected template + coverage,
              classification override, paid-through date — as a persistent,
              labeled band so the reviewer can see and act on them. When the
              flag is off, the prior chip-row header renders unchanged. */}
          {featureFlags.documentReviewRedesignEnabled ? (
            <ReviewContextBar
              totalRecords={displayTotalRecords}
              recordType={documentRecord?.record_type}
              requiresClient={requiresClient}
              selectedClient={selectedClient}
              onClientChange={setSelectedClient}
              template={template}
              uploadedByUserId={documentRecord?.uploaded_by_user_id_ref}
              uploadedDatetime={documentRecord?.uploaded_datetime}
              readOnly={!permissionsLoading && !canCreate}
              partnerReadOnly={!!partner?.readOnly}
              asOfState={asOfState}
              isChecking={isProjectionChecking}
              extractedRecords={loadedRecords}
              reviewProjection={reviewProjection}
              transformation={latestTransformation}
              classification={documentClassification}
              isClassificationLoading={isClassificationLoading}
              isClassificationSaving={isClassificationSaving}
              isClassificationReprocessing={isClassificationReprocessing}
              classificationError={classificationError}
              onClassificationOverride={handleClassificationOverride}
              reportedAsOf={reportedAsOf}
              onAsOfChange={handleAsOfChange}
              asOfSource={asOfSource}
              asOfFlagged={asOfFlagged}
              projectionUnavailable={projectionUnavailable}
              onReviewMapping={() => setMappingDialogOpen(true)}
              onReprocess={canEdit ? handleReprocess : undefined}
              excludedCount={reviewerDivergenceCount}
              isReprocessing={isClassificationReprocessing}
              onApprove={openApproveDialog}
              onReject={() => setRejectDialogOpen(true)}
              onDeleteDocument={canCascadeDelete ? () => setDeleteDialogOpen(true) : undefined}
              isApproving={isApproving}
              isRejecting={isRejecting}
              isReadyForApproval={isReadyForApproval}
              hasTransformation={hasTransformation}
              isClientMissing={isClientMissing}
              errorCount={errorCount}
              unresolvedErroredCount={unresolvedErrored.count}
              unresolvedCountIsExact={unresolvedErrored.exact}
              recordSummary={recordSummary}
              warningCount={warningCount}
              mappingBlockingCount={mappingValidationIssues.length}
              completeness={completeness}
              mappingAdvisoryCount={requiredAdvisory.length}
            />
          ) : (
            <DocumentReviewHeader
              totalRecords={displayTotalRecords}
              recordType={documentRecord?.record_type}
              extractedRecords={loadedRecords}
              reviewProjection={reviewProjection}
              classification={documentClassification}
              isClassificationLoading={isClassificationLoading}
              isClassificationSaving={isClassificationSaving}
              isClassificationReprocessing={isClassificationReprocessing}
              classificationError={classificationError}
              onClassificationOverride={handleClassificationOverride}
              reportedAsOf={reportedAsOf}
              onAsOfChange={handleAsOfChange}
              onReviewMapping={() => setMappingDialogOpen(true)}
              onReprocess={canEdit ? handleReprocess : undefined}
              excludedCount={reviewerDivergenceCount}
              isReprocessing={isClassificationReprocessing}
              onApprove={openApproveDialog}
              onReject={() => setRejectDialogOpen(true)}
              onDeleteDocument={canCascadeDelete ? () => setDeleteDialogOpen(true) : undefined}
              isApproving={isApproving}
              isRejecting={isRejecting}
              isReadyForApproval={isReadyForApproval}
              hasTransformation={hasTransformation}
              isClientMissing={isClientMissing}
              errorCount={errorCount}
              unresolvedErroredCount={unresolvedErrored.count}
              unresolvedCountIsExact={unresolvedErrored.exact}
              recordSummary={recordSummary}
              warningCount={warningCount}
              mappingBlockingCount={mappingValidationIssues.length}
              completeness={completeness}
              mappingAdvisoryCount={requiredAdvisory.length}
            />
          )}

          {/* Sticky approve bar while Compare with source hides the sidebar
              (#7431 AC3, artboard 1c). Sits BELOW the header, so it only
              takes over once the header's own Approve has scrolled away. */}
          {sourcePreviewOpen && (
            <StickyApproveBar
              status={approveBlocked ? { ...approveStatus, blocked: true } : approveStatus}
              isApproving={isApproving}
              onApprove={openApproveDialog}
              onBackToChecks={() => onSourcePreviewChange(false)}
            />
          )}

          {/* Main grid card — shrinks to fit, table scrolls internally */}
          <Paper
            elevation={0}
            sx={{
              minWidth: 0,
              overflow: "hidden",
              border: 1,
              borderColor: "divider",
              borderRadius: BORDER_RADIUS.XL,
              // Visually mute the grid when the client gate is unsatisfied
              opacity: isClientMissing ? 0.5 : 1,
              pointerEvents: isClientMissing ? "none" : "auto",
              transition: "opacity 150ms",
            }}
          >
            {/* Schema tabs inside the card. Show-all-columns toggle lives on
                the same row, opposite the tabs, so it visually anchors to the
                grid it controls without crowding the page-level toolbar above. */}
            {/* ABOVE the tabs, deliberately. An errors affordance inside a
                tab cannot show errors that live in another one, which is the
                blind spot that killed the "Next error" stepper (#6853). */}
            {/* Row decisions (#6921) are rail lines since #7432 — see buildReviewChecks. */}

            {/* Errored records moved into the rail (#7432): one card, one row per
                reason, Show in grid / Exclude N. The exclude confirm below is
                still what a row's Exclude opens. */}
            <Dialog open={!!pendingExcludeGroup} onClose={() => setPendingExcludeGroup(null)} maxWidth="xs" fullWidth>
              <DialogTitle>
                {/* The count is net of exclusions and, on a truncated errored
                    read, says so — matching the button that opened it. It read
                    a bare group total, which on a 5,000-error document
                    confirmed "Exclude 1,000 records?" and left 4,000 blocking
                    (!4682 review). */}
                Exclude {pendingExcludeCount.toLocaleString()} {pendingExcludeCount === 1 ? "record" : "records"}
                {erroredRecords.isTruncated ? ` of ${serverUnresolved.toLocaleString()}` : ""}?
              </DialogTitle>
              <DialogContent>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {pendingExcludeGroup
                    ? `${pendingExcludeCount === 1 ? "1 row" : `${pendingExcludeCount.toLocaleString()} rows`}: ${pendingExcludeGroup.label}`
                    : null}
                </Typography>
                {/* Names the alternative, because on a mis-mapped column (or a
                    file that belongs to another term) this is the wrong action
                    and discards real data. */}
                <Typography variant="body2" color="text.secondary">
                  {pendingExcludeGroup?.why}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setPendingExcludeGroup(null)}>Cancel</Button>
                <Button color="error" variant="contained" onClick={confirmExcludeGroup}>
                  {/* Net of exclusions, like the title and the button that
                      opened this. It read the raw group total. */}
                  Exclude {pendingExcludeCount.toLocaleString()}
                </Button>
              </DialogActions>
            </Dialog>

            <Box
              sx={{
                px: 2.5,
                pt: 2,
                pb: 1,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <StyledTabs value={selectedTab} onChange={(_e, val: number) => selectTab(val)}>
                {availableSchemas.map((schema) => {
                  const severity = issueSeverityBySchema[schema];
                  return (
                    <StyledTab
                      key={schema}
                      label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          {SCHEMA_LABELS[schema] || schema} ({(displayedRecordCounts[schema] || 0).toLocaleString()})
                          {severity === "error" && <ErrorIcon sx={{ fontSize: 14, color: "error.main" }} />}
                          {severity === "warning" && <WarningIcon sx={{ fontSize: 14, color: "warning.main" }} />}
                        </Box>
                      }
                    />
                  );
                })}
              </StyledTabs>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                {/* Paging sits beside the tabs (walkthrough feedback 2026-10-15),
                    not on its own row. Counts come from the server summary, so
                    "of N" is the whole document even though the grid holds one page. */}
                {hasMoreRecords && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                  >
                    Showing {loadedRecords.length.toLocaleString()} of {totalStoredRecords.toLocaleString()}
                    <Button
                      size="small"
                      variant="text"
                      onClick={loadMoreRecords}
                      disabled={isLoadingRecords}
                      sx={{ minWidth: 0, px: 0.75 }}
                    >
                      {isLoadingRecords ? "Loading..." : "Load more"}
                    </Button>
                  </Typography>
                )}
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showAllColumns}
                      onChange={(e) => setShowAllColumns(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Show all columns</Typography>}
                  sx={{ mr: 0 }}
                />
              </Box>
            </Box>

            {/* Search + grid inside the card. overflowX:auto here makes THIS
                Box the horizontal-scroll boundary for the table — so when the
                document has more columns than fit in the column's width, the
                table scrolls inside the card without pushing the card itself
                (or the issues sidebar) out of place. minWidth:0 prevents this
                Box from sizing to its content width, which would otherwise
                blow out the flex column above. */}
            {/* Forecast-scope notes on a paged document — one caption line,
                only when there is something to say (#6755). */}
            {hasMoreRecords &&
              (isProjectionWindowed || isProjectionSkipped || reviewProjection?.family_rollup_unavailable) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", px: 2.5, pb: 1 }}>
                  {[
                    isProjectionWindowed
                      ? forecastCoveredRecords !== null
                        ? `Forecasts cover the first ${forecastCoveredRecords.toLocaleString()} loaded records.`
                        : "Forecasts cover the loaded records."
                      : null,
                    // Family pools sum across MEMBERS, and a window can split a
                    // family even when no claimant is split — so the server
                    // suppresses pool figures rather than showing an understated total.
                    reviewProjection?.family_rollup_unavailable
                      ? "Family-deductible totals and stop-loss verdicts aren't shown on a windowed view — the family pool is evaluated in full when the document loads."
                      : null,
                    isProjectionSkipped ? "Forecasts are not calculated for documents this large." : null,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                </Typography>
              )}
            <Box sx={{ px: 2.5, pb: 2, minWidth: 0, overflowX: "auto" }} data-testid="review-grid">
              <RecordReviewGrid
                toolbarStart={
                  gridFilter ? (
                    // One chip, one way out: the chip IS the filter state, and its × clears it.
                    <Chip
                      size="small"
                      label={`Filtered to ${gridFilter.label}`}
                      onDelete={() => setGridFilter(null)}
                      deleteIcon={<CloseIcon aria-label="Clear filter" role="button" />}
                    />
                  ) : undefined
                }
                records={displayedRecordsForCurrentSchema}
                targetSchema={currentSchema}
                columns={displayedColumns}
                onViewSource={hasSourcePreview ? handleOpenSourcePreview : undefined}
                sourceType={isPdfDoc ? "pdf" : "spreadsheet"}
                isExcluded={isClaimantGrid ? isClaimantExcluded : isRecordExcluded}
                totalRecordCount={totalStoredRecords}
                hasMoreRecords={hasMoreRecords}
                // No selection, no Exclude for a viewer (#7433 AC1): the grid
                // derives its checkboxes from these two, so omitting them is
                // the whole gate. Download, expand, Row links and Compare with
                // source are independent of it.
                // The Claims tab's selection acts on lines only (no cascade to
                // the claimant and its siblings); the other tabs cascade.
                onBulkExclude={
                  canFix ? (records) => handleBulkExclude(records, { cascade: !isClaimLinesTab }) : undefined
                }
                onBulkRestore={
                  canFix ? (records) => handleBulkRestore(records, { cascade: !isClaimLinesTab }) : undefined
                }
                expandRow={isTopSchema || currentSchema === TargetSchema.REIMBURSEMENT ? renderExpandRow : undefined}
                expandLabel={
                  isTopSchema || currentSchema === TargetSchema.REIMBURSEMENT ? renderExpandLabel : undefined
                }
                isExpandable={isTopSchema || currentSchema === TargetSchema.REIMBURSEMENT ? isRowExpandable : undefined}
                forecastGetter={isTopSchema ? getProjectionForRecord : undefined}
                isProjectionLoading={isTopSchema ? isProjectionLoading : undefined}
                isFamilyDeductiblePolicy={
                  isTopSchema
                    ? typeof reviewProjection?.family_deductible === "number" && reviewProjection.family_deductible > 0
                    : undefined
                }
                claimantGrid={isClaimantGrid ? claimantGridContext : undefined}
                // Claim lines tab in the redesign's shape (artboard 2a); the
                // dynamic field table stays behind "Show all columns".
                claimLinesGrid={
                  currentSchema === TargetSchema.CLAIM_ITEM && !isReimbursementDoc && !showAllColumns
                    ? { policyWindows: reviewProjection?.policy_windows, outsidePolicyAcknowledged }
                    : undefined
                }
                documentRecordType={isTopSchema ? documentRecord?.record_type : undefined}
                reimbursementSummaryGetter={isTopSchema ? getSummaryForRecord : undefined}
                reimbursementRowGetter={getReimbursementRowForRecord}
                claimItemRowGetter={getClaimItemRowForRecord}
              />
            </Box>
          </Paper>
        </Box>

        {/* The "Before you approve" rail (#7432): Approve + status, then every
            check as a card or a pass line. Hidden while Compare with source is
            open; the sticky bar above the grid stands in. */}
        {showSidebar && (
          <Box sx={{ width: 360, flexShrink: 0, alignSelf: "flex-start" }}>
            <BeforeYouApproveRail
              status={approveBlocked ? { ...approveStatus, blocked: true } : approveStatus}
              checks={reviewChecks}
              isApproving={isApproving}
              onApprove={openApproveDialog}
              canFix={canFix}
              onAcknowledge={acknowledgements.acknowledge}
              onReviewMapping={canFix ? () => setMappingDialogOpen(true) : undefined}
              onReprocess={canFix && canEdit ? () => setRailReprocessConfirmOpen(true) : undefined}
              isReprocessing={isClassificationReprocessing}
              fixDisabledReason={fixDisabledReason}
              scopeNote={railScopeNote}
            />
          </Box>
        )}
      </Box>

      {/* Field Mapping Dialog */}
      {latestTransformation && clientId && (
        <FieldMappingDialog
          open={mappingDialogOpen}
          onClose={() => setMappingDialogOpen(false)}
          transformation={latestTransformation}
          transformationTemplates={template ? [template] : []}
          mappingValidationIssues={mappingValidationIssues}
          clientId={clientId}
          documentId={documentRecord?.id}
          advisoryContext={criticalFieldAdvisoryContext}
          isSpreadsheet={isSpreadsheetDocument(documentRecord)}
          isPdf={isPdfDocument(documentRecord)}
        />
      )}

      <RejectDocumentDialog
        open={rejectDialogOpen}
        documentName={documentRecord?.name}
        isRejecting={isRejecting}
        onCancel={() => setRejectDialogOpen(false)}
        onConfirm={async (input) => {
          if (await handleReject(input)) setRejectDialogOpen(false);
        }}
      />

      <ApproveConfirmDialog
        open={approveDialogOpen}
        isApproving={isApproving}
        onClose={() => setApproveDialogOpen(false)}
        onConfirm={() => {
          void handleApprove().finally(() => setApproveDialogOpen(false));
        }}
        subtitle={approveSubtitle}
        summary={approveSummary}
        mapping={mappingRecap}
        paidThroughLabel={
          asOfState.showAsOf && reportedAsOf ? formatDate(reportedAsOf, DATE_FORMATS.DISPLAY_LONG) : null
        }
        layoutLabel={template?.name ?? "Automatic extraction"}
        acknowledgementCount={placedAcknowledgements.length}
      />

      <ConfirmationDialog
        open={railReprocessConfirmOpen}
        title="Reprocess this document?"
        message={reprocessConfirmCopy(reviewerDivergenceCount).message}
        confirmLabel={ConfirmLabelType.Continue}
        cancelLabel={CancelLabelType.Cancel}
        confirmColor={reviewerDivergenceCount > 0 ? ColorType.Warning : ColorType.Primary}
        onConfirm={() => {
          setRailReprocessConfirmOpen(false);
          void handleReprocess();
        }}
        onCancel={() => setRailReprocessConfirmOpen(false)}
      />

      {documentRecord?.id && (
        <DeleteImpactDialog
          open={deleteDialogOpen}
          documentId={String(documentRecord.id)}
          documentName={documentRecord.name}
          onClose={() => setDeleteDialogOpen(false)}
          onDeleted={() => redirect("list", ResourceName.DOCUMENTS)}
          onCascadeStarted={() => redirect("list", ResourceName.DOCUMENTS)}
        />
      )}

      {/* Source Preview Drawers — only one renders based on document type */}
      {isSpreadsheetDoc && (
        <SourcePreviewDrawer
          open={sourcePreviewOpen}
          onClose={() => onSourcePreviewChange(false)}
          sheetData={sourcePreview.sheetData}
          highlightsFor={sourcePreview.highlightsFor}
          highlightedThroughRow={sourcePreview.highlightedThroughRow}
          hasMoreRecords={hasMoreRecords}
          sheetName={sourcePreview.sheetName}
          headerRowIndex={sourcePreview.headerRowIndex}
          isLoading={sourcePreview.isLoading}
          error={sourcePreview.error}
          focusRow={sourcePreviewFocusRow}
          templateUnavailable={hasTransformation && !template?.field_mappings?.length}
        />
      )}

      {isPdfDoc && (
        <PdfSourcePreviewDrawer
          open={sourcePreviewOpen}
          onClose={() => onSourcePreviewChange(false)}
          pdfData={pdfSourcePreview.pdfData}
          isLoading={pdfSourcePreview.isLoading}
          error={pdfSourcePreview.error}
          focusPage={pdfFocusPage}
          pageCount={latestTransformation?.processing_metadata?.document_pages ?? null}
          highlights={pdfHighlights}
        />
      )}
    </Box>
  );
};
